import React from 'react'
import Paper from '@material-ui/core/Paper'
import styled from 'styled-components'

import 'firebaseui/dist/firebaseui.css'
import 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/messaging'

import Title from '../../atomics/Typography/Title'
import ButtonDark from '../../molecules/ThemeModeButton/ButtonDark'
import useFirebaseAuth from '../../../hooks/useFirebaseAuth'
import AuthValid from '../../../auth/AuthValid/AuthValid'
import Message from './Message'

const Right = styled('div')`
  margin-top: 2%;
  margin-right: 2%;
  position: absolute;
  top: 0;
  right: 0;
  background-color: ${({ theme }) => theme.palette.third.dark};
`

const Layout = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 90%;

  @media (max-width: 700px) {
    width: 90%;
    height: 100%;
    margin-top: 15px;
  }
`

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
`

const PaperContainer = styled(Paper)`
  padding: 3rem;
  max-width: 640px;
  @media (max-width: 700px) {
    max-width: 100%;
    padding: 1rem;
    margin-bottom: 2rem;
  }
`

const Divider = styled('div')`
  height: 1rem;
`

const TitleApp = styled(Title)`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 24px;
  font-style: light;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: 0.04em;
  text-align: center;
  white-space: nowrap;
  margin-bottom: 8%;
`

const Center = styled('div')`
  flex: 1;
  justify-content: center;
  align-items: baseline;
  display: flex;
  @media (max-width: 700px) {
    margin-top: 3rem;
  }
`

const LayoutLogin = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: row;
  height: 100vh;
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.palette.third.dark};
  @media (max-width: 700px) {
    flex-direction: column;
  }
`

const Logo = styled('img')`
  height: 180px;
  width: 180px;
  border: 1px solid #c1c1c1;
  @media (max-width: 700px) {
    height: 80px;
    width: 80px;
  }
  @media (max-width: 500px) {
    height: 40px;
    width: 40px;
  }
`

const TitleLogo = styled(Title)`
  font-size: 24px;
  line-height: 26px;
  font-style: light;
  font-weight: 200;
  letter-spacing: 0.04em;
  text-align: center;
  margin-bottom: 10%;

  color: ${({ theme }) => theme.palette.third.main};
  @media (max-width: 800px) {
    padding-left: 0;
  }
  @media (max-width: 700px) {
    margin-bottom: 10px;
    margin-top: 10px;
  }
`

const SubTitleLogo = styled(TitleLogo)`
  font-size: 18px;
  line-height: 24px;
  margin-top: 10px;
  color: #b7b7b7;
  @media (max-width: 800px) {
    padding-left: 0;
  }
  @media (max-width: 700px) {
    margin-bottom: 10px;
    margin-top: 10px;
  }
`

const SubLayoutLogin = styled('div')`
  display: flex;
  width: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: center;
`

const SubLayoutLoginRight = styled(SubLayoutLogin)`
  background-color: ${({ theme }) => theme.palette.third.light};
`

const TitleContainer = styled('div')`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  @media (max-width: 800px) {
    margin-top: 10px;
    margin-bottom: 10px;
  }
`
/**
 * LoginSection component for the login page.
 *
 * @param {Object} props - The props for the component.
 * @param {boolean} props.loading - Indicates whether the loading state is active.
 * @param {string} [props.title='BackOffice'] - The title to display in the login Loginsection.
 * @param {string} [props.subtitle='Marketplace'] - The subtitle to display in the login Loginsection.
 * @returns {JSX.Element} The rendered LoginSection component.
 */
const LoginSection = ({
  loading,
  title = 'BackOffice',
  subtitle = 'Marketplace'
}) => {
  return (
    <SubLayoutLogin>
      <TitleContainer>
        <TitleLogo variant="h1">{title}</TitleLogo>
        {loading ? (
          <AuthValid />
        ) : (
          <Logo
            alt="logo"
            src="https://cdn.shopify.com/s/files/1/0104/4391/5319/files/kitchencenter_logo.svg?6927"
          />
        )}
      </TitleContainer>
      <SubTitleLogo variant="h2">{subtitle}</SubTitleLogo>
    </SubLayoutLogin>
  )
}

/**
 * UIGoogle is a functional React component that renders a UI Loginsection
 * for Google authentication. It includes multiple dividers for spacing
 * and a container for Firebase UI authentication.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 */
const UIGoogle = () => (
  <>
    <Divider />
    <Divider />
    <Center>
      <div id="firebaseui-auth-container"></div>
    </Center>
    <Divider />
    <Divider />
  </>
)

const LoginHeader = () => (
  <Right>
    <ButtonDark />
  </Right>
)

const TitleLogin = ({ title }) => <TitleApp variant="h3">{title}</TitleApp>

const LoginForm = ({ title = 'Iniciar sesión' }) => {
  const { errorText, loading } = useFirebaseAuth()
  return (
    <PaperContainer elevation={3} role="form">
      <TitleLogin title={title} />
      <Message errorText={errorText} />
      {loading && <AuthValid />}
      <div style={{ display: loading ? 'none' : 'block' }}>
        <UIGoogle />
      </div>
    </PaperContainer>
  )
}

/**
 * LoginContainer component renders the login page layout with a title, error message,
 * and a Google login button. It is wrapped in a styled layout and container.
 *
 * @returns {JSX.Element} The rendered LoginContainer component.
 */
const LoginContainer = () => (
  <SubLayoutLoginRight>
    <Layout>
      <Container>
        <LoginHeader />
        <LoginForm title="Iniciar sesión" />
      </Container>
    </Layout>
  </SubLayoutLoginRight>
)

/**
 * Login component that renders the login page layout.
 * It utilizes Firebase authentication to handle user login state.
 *
 * @component
 * @returns {JSX.Element} The rendered Login component.
 *
 * @example
 * // Usage
 * <Login />
 *
 * @remarks
 * - The component uses `useFirebaseAuth` to retrieve authentication state.
 * - Displays a loading indicator and error messages if applicable.
 *
 * @dependencies
 * - LayoutLogin: The layout wrapper for the login page.
 * - LoginSection: A component to display loading state.
 * - LoginContainer: A component to handle and display login form and error messages.
 */
const Login = () => {
  return (
    <LayoutLogin>
      <LoginSection />
      <LoginContainer />
    </LayoutLogin>
  )
}
export default Login
